import type { FamilyBreakdown, FamilyPaxSummary } from '../schemas/family-breakdown.js';

export type BreakdownFamily = FamilyBreakdown['families'][number];

export const familyDisplayName = (family: BreakdownFamily, index: number): string => {
  const label = family.label?.trim();
  return label ? label : `Family ${index + 1}`;
};

export const familyPaxLabel = (pax: FamilyPaxSummary): string => {
  const parts: string[] = [];
  if (pax.adults > 0) parts.push(`${pax.adults}A`);
  const withBed = pax.children - pax.childrenNoBed;
  if (withBed > 0) parts.push(`${withBed}C`);
  if (pax.childrenNoBed > 0) parts.push(`${pax.childrenNoBed}C (no bed)`);
  if (pax.infants > 0) parts.push(`${pax.infants} Inf`);
  return parts.join(' + ');
};

// Largest-remainder split of a non-negative amount.
const splitByWeights = (amount: bigint, weights: readonly bigint[]): bigint[] => {
  const clean = weights.map((w) => (w > 0n ? w : 0n));
  const cleanTotal = clean.reduce((s, w) => s + w, 0n);
  const ws = cleanTotal === 0n ? clean.map(() => 1n) : clean;
  const total = cleanTotal === 0n ? BigInt(ws.length) : cleanTotal;
  const parts = ws.map((w) => (amount * w) / total);
  let rest = amount - parts.reduce((s, p) => s + p, 0n);
  const order = ws
    .map((w, i) => ({ i, rem: (amount * w) % total }))
    .sort((a, b) => (a.rem === b.rem ? a.i - b.i : a.rem > b.rem ? -1 : 1));
  for (const o of order) {
    if (rest === 0n) break;
    parts[o.i]! += 1n;
    rest -= 1n;
  }
  return parts;
};

export const familyRoomTotalsPaise = (family: BreakdownFamily): bigint[] => {
  if (family.rooms.length === 0) return [];
  const total = BigInt(family.totalPaise);
  const weights = family.rooms.map((r) => {
    const n = BigInt(r.netCostPaise);
    return n > 0n ? n : 0n;
  });
  const neg = total < 0n;
  const abs = neg ? -total : total;
  const parts =
    abs % 100n === 0n
      ? splitByWeights(abs / 100n, weights).map((p) => p * 100n)
      : splitByWeights(abs, weights);
  return neg ? parts.map((p) => -p) : parts;
};

export const shouldShowFamilyBreakdown = (b: FamilyBreakdown | null | undefined): boolean => {
  if (!b || b.families.length === 0) return false;
  if (b.families.length === 1 && b.families[0]!.rooms.length <= 1) return false;
  return true;
};
